import { supabase, type InsertTables, type Tables } from '../../../lib/supabase';
import type { QueryRoute } from './agent';

export type FaqCacheRouteKind = QueryRoute['kind'];

type LocalFaqCacheEntry = {
  questionNormalized: string;
  routeKind: FaqCacheRouteKind;
  answer: string;
  toolNames: string[];
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  hitCount: number;
};

export type FaqCacheHit = {
  source: 'local' | 'shared';
  answer: string;
  routeKind: FaqCacheRouteKind;
  toolNames: string[];
  lastUsedAt: string;
  expiresAt: string;
};

type SharedFaqCacheRow = Tables<'faq_cache'>;

const LOCAL_STORAGE_KEY = 'electroplm.ai.faq-cache.v1';
const MAX_LOCAL_CACHE_ENTRIES = 60;
const FEATURE_REQUEST_PREFIX = '[feature request]';

const ROUTE_TTL_MS: Record<FaqCacheRouteKind, number> = {
  'ontology-only': 1000 * 60 * 60 * 24 * 30,
  'operational-only': 1000 * 60 * 5,
  mixed: 1000 * 60 * 60,
  'external-only': 1000 * 60 * 60 * 12,
};

function nowIso() {
  return new Date().toISOString();
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function isExpired(expiresAt: string) {
  return new Date(expiresAt).getTime() <= Date.now();
}

function cacheKey(questionNormalized: string, routeKind: FaqCacheRouteKind) {
  return `${routeKind}::${questionNormalized}`;
}

function readLocalCacheMap() {
  if (!isBrowser()) return new Map<string, LocalFaqCacheEntry>();

  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return new Map<string, LocalFaqCacheEntry>();

    const parsed = JSON.parse(raw) as LocalFaqCacheEntry[];
    const entries = parsed.filter((entry) => !isExpired(entry.expiresAt));
    return new Map(entries.map((entry) => [cacheKey(entry.questionNormalized, entry.routeKind), entry]));
  } catch {
    return new Map<string, LocalFaqCacheEntry>();
  }
}

function writeLocalCacheMap(entries: Map<string, LocalFaqCacheEntry>) {
  if (!isBrowser()) return;

  const serialized = [...entries.values()]
    .filter((entry) => !isExpired(entry.expiresAt))
    .sort((left, right) => new Date(right.lastUsedAt).getTime() - new Date(left.lastUsedAt).getTime())
    .slice(0, MAX_LOCAL_CACHE_ENTRIES);

  window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(serialized));
}

function toLocalHit(entry: LocalFaqCacheEntry): FaqCacheHit {
  return {
    source: 'local',
    answer: entry.answer,
    routeKind: entry.routeKind,
    toolNames: entry.toolNames,
    lastUsedAt: entry.lastUsedAt,
    expiresAt: entry.expiresAt,
  };
}

function toSharedHit(row: SharedFaqCacheRow): FaqCacheHit {
  return {
    source: 'shared',
    answer: row.answer,
    routeKind: row.route_kind,
    toolNames: row.tool_names || [],
    lastUsedAt: row.last_used_at,
    expiresAt: row.expires_at,
  };
}

function ttlForRoute(routeKind: FaqCacheRouteKind) {
  return ROUTE_TTL_MS[routeKind];
}

function nextExpiry(routeKind: FaqCacheRouteKind) {
  return new Date(Date.now() + ttlForRoute(routeKind)).toISOString();
}

export function normalizeFaqQuestion(question: string) {
  return question
    .replace(/\[context:[^\]]*\]\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function shouldCacheQuestion(question: string) {
  const normalized = normalizeFaqQuestion(question);
  if (!normalized || normalized.length < 12) return false;
  if (normalized.startsWith(FEATURE_REQUEST_PREFIX)) return false;
  return true;
}

export async function lookupLocalFaqCache(question: string, routeKind: FaqCacheRouteKind): Promise<FaqCacheHit | null> {
  const questionNormalized = normalizeFaqQuestion(question);
  if (!questionNormalized) return null;

  const entries = readLocalCacheMap();
  const key = cacheKey(questionNormalized, routeKind);
  const entry = entries.get(key);
  if (!entry) return null;
  if (isExpired(entry.expiresAt)) {
    entries.delete(key);
    writeLocalCacheMap(entries);
    return null;
  }

  const updatedEntry: LocalFaqCacheEntry = {
    ...entry,
    lastUsedAt: nowIso(),
    hitCount: entry.hitCount + 1,
  };
  entries.set(key, updatedEntry);
  writeLocalCacheMap(entries);
  return toLocalHit(updatedEntry);
}

export async function storeLocalFaqCache(params: {
  question: string;
  routeKind: FaqCacheRouteKind;
  answer: string;
  toolNames?: string[];
}) {
  const questionNormalized = normalizeFaqQuestion(params.question);
  if (!questionNormalized || !isBrowser()) return;

  const currentTime = nowIso();
  const entries = readLocalCacheMap();
  const key = cacheKey(questionNormalized, params.routeKind);
  const existing = entries.get(key);

  entries.set(key, {
    questionNormalized,
    routeKind: params.routeKind,
    answer: params.answer,
    toolNames: params.toolNames || [],
    createdAt: existing?.createdAt || currentTime,
    lastUsedAt: currentTime,
    expiresAt: nextExpiry(params.routeKind),
    hitCount: (existing?.hitCount || 0) + 1,
  });

  writeLocalCacheMap(entries);
}

async function bumpSharedCacheHit(row: SharedFaqCacheRow) {
  const hitCount = row.hit_count + 1;
  const lastUsedAt = nowIso();
  const expiresAt = nextExpiry(row.route_kind);

  await supabase
    .from('faq_cache')
    .update({ hit_count: hitCount, last_used_at: lastUsedAt, expires_at: expiresAt })
    .eq('id', row.id);
}

export async function lookupSharedFaqCache(question: string, routeKind: FaqCacheRouteKind): Promise<FaqCacheHit | null> {
  const questionNormalized = normalizeFaqQuestion(question);
  if (!questionNormalized) return null;

  const { data, error } = await supabase
    .from('faq_cache')
    .select('*')
    .eq('question_normalized', questionNormalized)
    .eq('route_kind', routeKind)
    .gt('expires_at', nowIso())
    .maybeSingle();

  if (error || !data) return null;

  void bumpSharedCacheHit(data);
  await storeLocalFaqCache({
    question,
    routeKind,
    answer: data.answer,
    toolNames: data.tool_names || [],
  });

  return toSharedHit(data);
}

export async function lookupFaqCache(question: string, routeKind: FaqCacheRouteKind): Promise<FaqCacheHit | null> {
  const localHit = await lookupLocalFaqCache(question, routeKind);
  if (localHit) return localHit;
  return lookupSharedFaqCache(question, routeKind);
}

export async function storeSharedFaqCache(params: {
  question: string;
  routeKind: FaqCacheRouteKind;
  answer: string;
  toolNames?: string[];
}) {
  const questionNormalized = normalizeFaqQuestion(params.question);
  if (!questionNormalized) return;

  const { data: existing } = await supabase
    .from('faq_cache')
    .select('*')
    .eq('question_normalized', questionNormalized)
    .eq('route_kind', params.routeKind)
    .maybeSingle();

  const currentTime = nowIso();
  const payload: InsertTables<'faq_cache'> = {
    question_normalized: questionNormalized,
    question_original: params.question.trim(),
    route_kind: params.routeKind,
    answer: params.answer,
    tool_names: params.toolNames || [],
    hit_count: (existing?.hit_count || 0) + 1,
    last_used_at: currentTime,
    expires_at: nextExpiry(params.routeKind),
  };

  await supabase
    .from('faq_cache')
    .upsert(payload, { onConflict: 'question_normalized,route_kind' });
}

export async function storeFaqCache(params: {
  question: string;
  routeKind: FaqCacheRouteKind;
  answer: string;
  toolNames?: string[];
}) {
  await storeLocalFaqCache(params);
  void storeSharedFaqCache(params);
}